import { useState } from 'react';

import { Divider } from '@thefarmersfront/kitchen';

import dayjs, { Dayjs } from 'dayjs';

import { toSectionItemData } from './utils';
import { useGetDashboardDeliveryStatusQuery } from '~/hooks/delivery-agency/queries';

import { PATH } from '~/constants';

import { DeliveryAgencyDeliveryStatusDeliveryIssue } from '../DeliveryAgencyDeliveryStatusDeliveryIssue';
import { DeliveryAgencyDeliveryStatusDeliveryProgress } from '../DeliveryAgencyDeliveryStatusDeliveryProgress';
import { DeliveryAgencyDeliveryStatusRealtimeHeader } from '../DeliveryAgencyDeliveryStatusRealtimeHeader';
import { DeliveryAgencyDeliveryStatusTotalShippingLabels } from '../DeliveryAgencyDeliveryStatusTotalShippingLabels';
import { DeliveryTypeSection } from '../DeliveryTypeSection';
import { SectionItem } from '../SectionItem';

import styles from './DeliveryAgencyDeliveryStatusRealtime.module.scss';

const NIGHT_COLORS = { primary: '#4EB595', secondary: '#029568', error: '#E54445' };
const DAWN_COLORS = { primary: '#BD76FF', secondary: '#8D4CC4', error: '#E54445' };
const DAY_COLORS = { primary: '#5B8DEF', secondary: '#065DBA' };

const getDefaultDate = () => {
  const now = dayjs();
  return now.hour() < 12 ? now : now.add(1, 'day');
};

const DeliveryAgencyDeliveryStatusRealtime = () => {
  const [estimatedDeliveryDate, setEstimatedDeliveryDate] = useState<Dayjs>(getDefaultDate);

  const { data } = useGetDashboardDeliveryStatusQuery({
    params: { deliveryDate: estimatedDeliveryDate.format('YYYY-MM-DD') },
  });

  const { summary, nightDelivery, dawnDelivery, dayDelivery } = data?.data ?? {};

  const navigateToTracking = (deliveryType: string, shippingLabelStatus?: string) => {
    const params = new URLSearchParams({
      deliveryDate: estimatedDeliveryDate.format('YYYY-MM-DD'),
      deliveryType,
      ...(shippingLabelStatus ? { shippingLabelStatus } : {}),
    });
    window.open(`${PATH.DELIVERY_AGENCY_TRACKING}?${params}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className={styles.section}>
      <DeliveryAgencyDeliveryStatusRealtimeHeader
        estimatedDeliveryDate={estimatedDeliveryDate}
        onChangeEstimatedDeliveryDate={setEstimatedDeliveryDate}
      />
      <div className={styles.summary}>
        <DeliveryAgencyDeliveryStatusTotalShippingLabels
          totalCount={summary?.totalCount ?? 0}
          nightDeliveryCount={summary?.nightDeliveryCount ?? 0}
          dawnDeliveryCount={summary?.dawnDeliveryCount ?? 0}
          dayDeliveryCount={summary?.dayDeliveryCount ?? 0}
        />
        <DeliveryAgencyDeliveryStatusDeliveryProgress
          deliveryAccepted={summary?.deliveryAccepted}
          deliveryCompleted={summary?.deliveryCompleted}
          deliveryFailed={summary?.deliveryFailed}
        />
        <DeliveryAgencyDeliveryStatusDeliveryIssue
          deliveryCanceled={summary?.deliveryCanceled}
          notInNmPickUpOffice={summary?.notInNmPickUpOffice}
          deliveryDelayed={summary?.deliveryDelayed}
        />
      </div>

      <Divider color="gray100" />

      <div className={styles['delivery-type']}>
        <DeliveryTypeSection title="당일배송" onClickNavigate={() => navigateToTracking('NIGHT')}>
          <SectionItem
            description="Hub입고(집화)"
            totalRate={nightDelivery?.inNmPickUpOffice?.rate ?? 0}
            data={toSectionItemData(nightDelivery?.inNmPickUpOffice, NIGHT_COLORS.primary, NIGHT_COLORS.secondary)}
          />
          {nightDelivery?.inDeliveryCenter != null && (
            <SectionItem
              description="TC 도착"
              totalRate={nightDelivery.inDeliveryCenter.rate}
              data={toSectionItemData(nightDelivery.inDeliveryCenter, NIGHT_COLORS.primary)}
            />
          )}
          <SectionItem
            description="배송 출발"
            totalRate={nightDelivery?.deliveryStart?.rate ?? 0}
            data={toSectionItemData(nightDelivery?.deliveryStart, NIGHT_COLORS.primary)}
          />
          <SectionItem
            description="배송 완료"
            totalRate={nightDelivery?.deliveryCompleted?.rate ?? 0}
            data={toSectionItemData(nightDelivery?.deliveryCompleted, NIGHT_COLORS.primary, NIGHT_COLORS.error)}
            onClickNavigate={() => navigateToTracking('NIGHT', 'DELIVERY_COMPLETED')}
          />
        </DeliveryTypeSection>
        <DeliveryTypeSection title="샛별배송" onClickNavigate={() => navigateToTracking('DAWN')}>
          <SectionItem
            description="Hub입고(집화)"
            totalRate={dawnDelivery?.inNmPickUpOffice?.rate ?? 0}
            data={toSectionItemData(dawnDelivery?.inNmPickUpOffice, DAWN_COLORS.primary, DAWN_COLORS.secondary)}
          />
          {dawnDelivery?.inDeliveryCenter != null && (
            <SectionItem
              description="TC 도착"
              totalRate={dawnDelivery.inDeliveryCenter.rate}
              data={toSectionItemData(dawnDelivery.inDeliveryCenter, DAWN_COLORS.primary)}
            />
          )}
          <SectionItem
            description="배송 출발"
            totalRate={dawnDelivery?.deliveryStart?.rate ?? 0}
            data={toSectionItemData(dawnDelivery?.deliveryStart, DAWN_COLORS.primary)}
          />
          <SectionItem
            description="배송 완료"
            totalRate={dawnDelivery?.deliveryCompleted?.rate ?? 0}
            data={toSectionItemData(dawnDelivery?.deliveryCompleted, DAWN_COLORS.primary, DAWN_COLORS.error)}
            onClickNavigate={() => navigateToTracking('DAWN', 'DELIVERY_COMPLETED')}
          />
        </DeliveryTypeSection>
        <DeliveryTypeSection title="하루배송(택배)" onClickNavigate={() => navigateToTracking('DAY')}>
          <SectionItem
            description="Hub입고(집화)"
            totalRate={dayDelivery?.inNmPickUpOffice?.rate ?? 0}
            data={toSectionItemData(dayDelivery?.inNmPickUpOffice, DAY_COLORS.primary, DAY_COLORS.secondary)}
          />
          {dayDelivery?.inCourierPickUpCenter != null && (
            <SectionItem
              description="택배사 인계"
              totalRate={dayDelivery.inCourierPickUpCenter.rate}
              data={toSectionItemData(dayDelivery.inCourierPickUpCenter, DAY_COLORS.primary)}
            />
          )}
          <SectionItem
            description="배송 출발"
            totalRate={dayDelivery?.deliveryStart?.rate ?? 0}
            data={toSectionItemData(dayDelivery?.deliveryStart, DAY_COLORS.primary)}
          />
          <SectionItem
            description="배송 완료"
            totalRate={dayDelivery?.deliveryCompleted?.rate ?? 0}
            data={toSectionItemData(dayDelivery?.deliveryCompleted, DAY_COLORS.primary)}
            onClickNavigate={() => navigateToTracking('DAY', 'DELIVERY_COMPLETED')}
          />
        </DeliveryTypeSection>
      </div>
    </section>
  );
};

export { DeliveryAgencyDeliveryStatusRealtime };
